import { type CSSObject, type Theme } from "@mui/material/styles";

import { type AccordionVariant } from "ui/accordion/accordion.types";

export const ACCORDION_VARIANTS: AccordionVariant[] = ["default", "primary", "secondary", "filter"];

/**
 * Tailwind's `tracking-widest`, the tracking the `default` eyebrow has always shipped. No theme slot
 * holds it — `typography.h5` and `overline` carry the eyebrow's flat 2px, which is wider and comes with
 * an uppercase transform this title does not have.
 */
export const TITLE_LETTER_SPACING = "0.1em";

/** The chevron ships at `size-5`, not at the 24px its own `viewBox` declares. */
export const CHEVRON_SIZE = 20;

/**
 * `primary` and `secondary` headers are content-width, so the chevron sits against the title; the
 * other two span the row and push it to the far edge. `AccordionSummary` pins `width: 100%`, so
 * `fit-content` has to be stated — leaving `width` alone would stretch all four.
 */
export const HEADER_WIDTH: Record<AccordionVariant, "100%" | "fit-content"> = {
  default: "100%",
  primary: "fit-content",
  secondary: "fit-content",
  filter: "100%",
};

/**
 * `filter` is Figma's `Popover/Item` (`21152:2237`): `subtitle2` in `text.primary`, untracked. The other
 * three keep their font-size inherited, which is why only `filter` states metrics.
 */
export const TITLE_STYLES: Record<AccordionVariant, (theme: Theme) => CSSObject> = {
  default: (theme) => ({
    fontWeight: 600,
    letterSpacing: TITLE_LETTER_SPACING,
    color: theme.palette.grey[500],
  }),
  primary: (theme) => ({
    fontWeight: 600,
    color: theme.palette.grey[900],
  }),
  secondary: (theme) => ({
    fontWeight: 600,
    color: theme.palette.primary.main,
  }),
  filter: (theme) => ({
    ...theme.typography.subtitle2,
    color: theme.palette.text.primary,
  }),
};

export const CHEVRON_COLOR = (theme: Theme) => theme.palette.primary.main;

/** `Popover/Item` turns its open caret from the brand aqua to the title's ink; the others keep one colour. */
export const EXPANDED_CHEVRON_COLOR: Partial<Record<AccordionVariant, (theme: Theme) => string>> = {
  filter: (theme) => theme.palette.text.primary,
};

/** The title-to-panel gap; `filter`'s is Figma's `popover/container/body/item/spacing`. */
export const DETAILS_PADDING_TOP: Record<AccordionVariant, number> = {
  default: 0.5,
  primary: 0.5,
  secondary: 0.5,
  filter: 0.75,
};
